import { HttpParams } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { AppConfigService } from '@config/app-config.service';
import { EventService } from '@event/event.service';
import { ContentCategory } from '@model/content-category';
import { ContentItem } from '@model/content-item';
import { ContentSearchType } from '@model/content-search-type';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from '@shared/constants';
import { appDatabase } from '@shared/database/app-database';
import { BaseService } from '@shared/service/base.service';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  Subscription,
  mergeMap,
  tap,
  timer,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ContentService extends BaseService {
  private _contentCategories = signal<ContentCategory[] | undefined>(undefined);
  private _contentItems = signal<ContentItem[] | undefined>(undefined);
  private _contentItemTable = appDatabase.contentItem;
  private _contentCategoryTable = appDatabase.contentCategory;

  private appConfigService = inject(AppConfigService);
  private eventService = inject(EventService);
  private translateService = inject(TranslateService);

  private timerSubscription?: Subscription;

  contentCategories = computed<ContentCategory[]>(() => {
    const contentCategories = this._contentCategories() ?? [];
    const contentItems = this._contentItems() ?? [];

    return contentCategories.map(category => {
      const categoryItems = contentItems.filter(
        item => item.categoryUuid === category.uuid,
      );
      const contentUuid =
        categoryItems.length === 1 ? categoryItems[0]?.uuid : undefined;

      return {
        ...category,
        contentUuid,
      };
    });
  });

  contentNewsItems = computed(() => {
    const contentItems = this._contentItems() ?? [];

    return contentItems
      .filter(contentItem => contentItem.isNews)
      .sort(
        (contentItem1, contentItem2) =>
          new Date(contentItem2.publishDate).getTime() -
          new Date(contentItem1.publishDate).getTime(),
      );
  });

  hasContentNewsItems = computed<boolean>(
    () => this.contentNewsItems().length > 0,
  );

  hasContentCategories = computed<boolean>(
    () => this.contentCategories().length > 0,
  );

  constructor() {
    super('content');

    effect(() => {
      // # App Init logic
      // - Check the available appConfig signal
      // - If no appConfig found, cancel the possible running refresh timer
      // - If there is an appConfig refresh the load of content every x minutes
      // - This x is taken from the appConfig or Constants
      // - Start the new timer to execute loadContentItems()

      this.timerSubscription?.unsubscribe();

      const appConfig = this.appConfigService.appConfig();

      if (appConfig && !this.timerSubscription) {
        const contentUpdateInterval =
          (appConfig.contentUpdateInterval ??
            Constants.CONTENT_ITEMS_UPDATE_INTERVAL_MINUTES) *
          Constants.A_MINUTE;

        this.timerSubscription = // Create a timer to update event settings
          timer(contentUpdateInterval, contentUpdateInterval)
            .pipe(mergeMap(() => this.loadContentItems()))
            .subscribe();
      }
    });

    effect(() => {
      const contentItems = this._contentItems();

      void this.processTableContentItems(contentItems);
    });

    effect(() => {
      const contentCategories = this._contentCategories();

      void this.processTableContentCategories(contentCategories);
    });
  }

  init(): Observable<void> {
    // Initial load of content in case of offline device
    // Load when database is ready, so starting offline will show the (available) local content
    appDatabase.on('ready', () => this.loadContent().subscribe());

    return of(undefined);
  }

  private loadContent(): Observable<void> {
    // Load the content categories and content items
    return this.loadCategories().pipe(
      tap(categories => this._contentCategories.set(categories)),
      mergeMap(() => this.loadContentItems()),
      tap(contentItems => this._contentItems.set(contentItems)),
      map(() => undefined),
    );
  }

  // Update the content items on resume of the app. Only when the event is NOT active
  // During the event the timerSubscription is responsible for refresh of the content
  onResume(): Observable<void> {
    const event = this.eventService.event();

    // Do not load the content on a refresh (on resume of app) when event is live.
    if (event?.isEventLive) {
      return of(undefined);
    }

    return this.loadContent();
  }

  private async processTableContentItems(
    contentItems?: ContentItem[],
  ): Promise<void> {
    // No processing when contentItems is undefined
    // Otherwise the stored data is deleted on app init
    if (contentItems !== undefined) {
      // Check the stored content items in the table against the new list,
      // bulkDelete by the found deleted uuids
      const storedContentItems = await this._contentItemTable.toArray();
      const deletedContentItems = storedContentItems.filter(
        storedContentItem =>
          contentItems.find(
            contentItem => contentItem.uuid === storedContentItem.uuid,
          ) === undefined,
      );
      const deletedContentItemUuids = deletedContentItems.map(
        deletedContentItem => deletedContentItem.uuid,
      );
      await this._contentItemTable.bulkDelete(deletedContentItemUuids);

      await this._contentItemTable.bulkPut(contentItems);
    }
  }

  private async processTableContentCategories(
    contentCategories?: ContentCategory[],
  ): Promise<void> {
    // No processing when contentCategories is undefined
    // Otherwise the stored data is deleted on app init
    if (contentCategories !== undefined) {
      // Check the stored content items in the table against the new list,
      // bulkDelete by the found deleted uuids
      const storedContentCategories =
        await this._contentCategoryTable.toArray();
      const deletedContentCategories = storedContentCategories.filter(
        storedContentCategory =>
          contentCategories.find(
            contentCategory =>
              contentCategory.uuid === storedContentCategory.uuid,
          ) === undefined,
      );
      const deletedContentCategoryUuids = deletedContentCategories.map(
        deletedContentCategory => deletedContentCategory.uuid,
      );
      await this._contentCategoryTable.bulkDelete(deletedContentCategoryUuids);

      await this._contentCategoryTable.bulkPut(contentCategories);
    }
  }

  search(searchType: ContentSearchType, query?: string): ContentItem[] {
    const isNews = searchType === 'news';

    const contentItems = (this._contentItems() ?? []).filter(
      contentItem => contentItem.isNews === isNews,
    );

    return query
      ? contentItems.filter(
          contentItem =>
            contentItem.body.toLowerCase().includes(query.toLowerCase()) ||
            contentItem.title.toLowerCase().includes(query.toLowerCase()),
        )
      : [];
  }

  getContentCategoryByCode(code?: string): ContentCategory | undefined {
    return (
      this.contentCategories().find(
        contentCategory => contentCategory.code === code,
      ) ?? undefined
    );
  }

  getContentItemsForCategoryCode(code?: string): ContentItem[] {
    const contentCategory = this.getContentCategoryByCode(code);

    return (this._contentItems() ?? []).filter(
      contentItem => contentItem.categoryUuid === contentCategory?.uuid,
    );
  }

  get(uuid: string): Observable<ContentItem> {
    const contentItems = this._contentItems() ?? [];
    const contentItem = contentItems.find(item => item.uuid === uuid);

    const params = this.getLanguageParams();

    return contentItem
      ? of(contentItem)
      : this.httpClient
          .get<ContentItem>(`${this.baseUrl}/items/${uuid}`, {
            params,
          })
          .pipe(
            tap(item =>
              this._contentItems.update(currentContentItems => [
                ...(currentContentItems ?? []),
                item,
              ]),
            ),
          );
  }

  private getLanguageParams(): HttpParams {
    return new HttpParams().append(
      'language',
      this.translateService.getCurrentLang(),
    );
  }

  private loadCategories(): Observable<ContentCategory[]> {
    const url = `${this.baseUrl}/categories`;

    return this.httpClient.get<ContentCategory[]>(url).pipe(
      catchError(() => {
        // On error of ContentCategory call the flow needs to continue.
        // Will return the content of the local storage table, so the content from the database is used in the app
        return from(this._contentCategoryTable.toArray());
      }),
    );
  }

  private loadContentItems(): Observable<ContentItem[]> {
    const url = `${this.baseUrl}/items`;
    const params = this.getLanguageParams();

    return this.httpClient.get<ContentItem[]>(url, { params }).pipe(
      catchError(() => {
        // On error of ContentItem call the flow needs to continue.
        // Will return the content of the local storage table, so the content from the database is used in the app
        return from(this._contentItemTable.toArray());
      }),
    );
  }
}
